// Servicio para manejar el cambio de estado de mensajería en conversaciones (optimista y rollback)
import ChatActionsModelMC from '../../presentation/models/inbox/ChatActionsModelMC';
import { updateOrInsertConversationOptimized } from '../../actions/inbox/conversationOptimizedActions';
import { isInboxMsChat } from './inboxMsChatPlatform';
import { assertMsInboxUrlConfigured } from './msInboxClient';
import { changeStatusMsInbox } from './msInboxChangeStatusClient';

/**
 * Valida que los datos necesarios estén presentes para cambiar el estado del chat.
 * @param {Array} body - Array con datos del chat y estado a cambiar.
 * @param {Function} dispatch - Función dispatch de Redux.
 * @returns {boolean} True si los datos son válidos, false en caso contrario.
 */
function validateChatData(body, dispatch) {
  if (!Array.isArray(body) || body.length === 0) {
    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: 'Invalid body format for chat status change'
    });
    return false;
  }

  // Validar todos los elementos del array
  for (let i = 0; i < body.length; i += 1) {
    const chatData = body[i];
    const { id, platform, status_chat } = chatData || {};
    const hasValidStatusChat = status_chat !== undefined && status_chat !== null;

    if (!id || !platform || !hasValidStatusChat) {
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: `Missing data to change chat status at index ${i}`
      });
      return false;
    }
  }
  return true;
}

/**
 * Actualiza el estado local de múltiples conversaciones de forma optimista.
 * @param {Function} dispatch - Función dispatch de Redux.
 * @param {Array} body - Array con datos de las conversaciones.
 * @returns {Promise<Array>} Array con resultados de cada actualización.
 */
async function updateMultipleLocalStates(dispatch, body) {
  return Promise.all(body.map(async (chatData) => {
    const { id, status_chat } = chatData;
    const updateResult = await dispatch(updateOrInsertConversationOptimized({
      id,
      changes: { status_chat }
    }));

    if (!updateResult.success) {
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: updateResult.error || `Error updating conversation state for id: ${id}`
      });
    }

    return { id, success: updateResult.success, error: updateResult.error };
  }));
}

/**
 * Realiza rollback de múltiples estados locales en caso de error del servidor.
 * @param {Function} dispatch - Función dispatch de Redux.
 * @param {Array} body - Array con datos de las conversaciones.
 * @param {Array} prevStatusChat - Array con estados previos [{ id, status_chat }, ...].
 * @param {Array} [failedIds=null] - IDs específicos que fallaron para rollback selectivo.
 * @returns {Promise<void>}
 */
async function rollbackMultipleLocalStates(dispatch, body, prevStatusChat, failedIds = null) {
  await Promise.all(body.map(async (chatData) => {
    const { id } = chatData;
    // Si se especifican IDs fallidos, solo hacer rollback de esos
    if (failedIds && !failedIds.includes(id)) return;

    // Buscar el estado previo correspondiente a este id
    const prevData = prevStatusChat.find((prev) => prev.id === id);
    if (prevData) {
      await dispatch(updateOrInsertConversationOptimized({
        id,
        changes: { status_chat: prevData.status_chat }
      }));
    }
  }));
}

/**
 * Maneja el rollback de chats que fallaron en el servidor.
 * @param {Function} dispatch - Función dispatch de Redux.
 * @param {Array} body - Array con datos de las conversaciones.
 * @param {Array} prevStatusChat - Array con estados previos [{ id, status_chat }, ...].
 * @param {Array} failedChats - IDs de chats que fallaron.
 * @param {Array} localResults - Resultados de las actualizaciones locales.
 * @returns {Promise<Object>} Resultado con información de fallos.
 */
async function handleFailedChatsRollback(dispatch, body, prevStatusChat, failedChats, localResults) {
  await rollbackMultipleLocalStates(dispatch, body, prevStatusChat, failedChats);
  dispatch({
    type: 'LOAD_DATA_FAILURE',
    error: 'Algunos chats no pudieron actualizarse en el servidor',
    failedChats
  });
  return {
    success: false,
    error: 'Algunos chats no pudieron actualizarse en el servidor',
    failedChats,
    results: localResults
  };
}

function partitionByPlatform(body) {
  const msBodies = body.filter((b) => isInboxMsChat(b?.platform));
  const legacyBodies = body.filter((b) => !isInboxMsChat(b?.platform));
  return { msBodies, legacyBodies };
}

function buildChangeStatusPayload(bodies) {
  const status_chat = bodies[0]?.status_chat;
  const chats = bodies.map(({ id, platform }) => ({ id, platform }));
  return { status_chat, chats };
}

async function sendToLegacyServer(bodies) {
  return ChatActionsModelMC.changeStatus(buildChangeStatusPayload(bodies));
}

async function sendToMsServer(bodies) {
  return changeStatusMsInbox(buildChangeStatusPayload(bodies));
}

async function sendToServers(legacyBodies, msBodies) {
  const hasLegacy = legacyBodies.length > 0;
  const hasMs = msBodies.length > 0;

  if (!hasLegacy && !hasMs) return { settled: null };

  if (hasMs) {
    assertMsInboxUrlConfigured();
  }

  if (hasLegacy && !hasMs) {
    const result = await sendToLegacyServer(legacyBodies);
    return { settled: null, legacyResult: result };
  }

  if (!hasLegacy && hasMs) {
    const result = await sendToMsServer(msBodies);
    return { settled: null, msResult: result };
  }

  const settled = await Promise.allSettled([
    sendToLegacyServer(legacyBodies),
    sendToMsServer(msBodies)
  ]);
  return { settled };
}

/**
 * Cambia el estado de mensajería de un chat y maneja la sincronización con el servidor.
 * Implementa actualización optimista con rollback en caso de error.
 *
 * @param {Function} dispatch - Función dispatch de Redux.
 * @param {Array} body - Array con datos del chat y estado a cambiar.
 * @param {Array} prevStatusChat - Array con estados previos [{ id, status_chat }, ...].
 * @returns {Promise<Object>} Resultado de la operación con éxito o error.
 */
export default async function handleChatMessagingState(dispatch, body, prevStatusChat) {
  const safePrevStatusChat = Array.isArray(prevStatusChat) ? prevStatusChat : [];

  // 1. Validar datos de entrada
  if (!validateChatData(body, dispatch)) {
    return { success: false, error: 'Invalid data to change chat status' };
  }

  // 2. Actualizar estado local de todas las conversaciones de forma optimista
  const updateResults = await updateMultipleLocalStates(dispatch, body);
  const failedUpdates = updateResults.filter((result) => !result.success);

  if (failedUpdates.length > 0) {
    return {
      success: false,
      error: 'Some conversations failed to update locally',
      failedUpdates
    };
  }

  // 3. Particionar por plataforma
  const { msBodies, legacyBodies } = partitionByPlatform(body);

  if (msBodies.length === 0 && legacyBodies.length === 0) {
    return { success: true, processedConversations: body.length, results: updateResults };
  }

  // 4. Enviar a servidor(es)
  try {
    const { settled, legacyResult, msResult } = await sendToServers(legacyBodies, msBodies);

    // Caso simple (un solo destino)
    if (!settled) {
      const result = legacyResult || msResult;
      if (result && Array.isArray(result.failed) && result.failed.length > 0) {
        const failedIds = result.failed.map((f) => f.id);
        return await handleFailedChatsRollback(dispatch, body, prevStatusChat, failedIds, updateResults);
      }
      return { success: true, processedConversations: body.length, results: updateResults };
    }

    // Caso mixto (Promise.allSettled): rollback selectivo
    const legacySettled = settled[0];
    const msSettled = settled[1];
    const legacyFailed = legacySettled.status === 'rejected';
    const msFailed = msSettled.status === 'rejected';

    if (legacyFailed || msFailed) {
      const failedIds = [
        ...(legacyFailed ? legacyBodies.map((b) => b.id) : []),
        ...(msFailed ? msBodies.map((b) => b.id) : [])
      ];
      await rollbackMultipleLocalStates(dispatch, body, prevStatusChat, failedIds);

      const msgParts = [];
      if (legacyFailed) msgParts.push(legacySettled.reason?.message || String(legacySettled.reason));
      if (msFailed) msgParts.push(msSettled.reason?.message || String(msSettled.reason));
      const errorMsg = msgParts.filter(Boolean).join(' | ') || 'Error changing chat status (batch)';

      dispatch({ type: 'LOAD_DATA_FAILURE', error: errorMsg });
      return { success: false, error: errorMsg, results: updateResults };
    }

    // Ambos exitosos: verificar failed items en respuestas
    const allResults = [legacySettled.value, msSettled.value].filter(Boolean);
    const allFailed = allResults.flatMap((r) => (Array.isArray(r.failed) ? r.failed : []));
    if (allFailed.length > 0) {
      const failedIds = allFailed.map((f) => f.id);
      return await handleFailedChatsRollback(dispatch, body, prevStatusChat, failedIds, updateResults);
    }

    return { success: true, processedConversations: body.length, results: updateResults };
  } catch (error) {
    // 4. Rollback en caso de error del servidor (todos los chats)
    await rollbackMultipleLocalStates(dispatch, body, safePrevStatusChat);

    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: error.message || 'An unexpected error occurred.'
    });

    return { success: false, error: error.message };
  }
}
